import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DropdownList from 'react-widgets/DropdownList';
import FormGroup from '../../../FormGroup';
import config from '../../../../config';
import CustomModal from '../../../Modal';
import { I18nFormatter } from '../../../../utils/I18n/I18n';

import PaymentSystem from '../PaymentSystems/PaymentSystem';

export const PaymentPageSettingModal = ({
    payment: propPayment,
    visible: propVisible,
    onClose: propOnClose,
    onSubmit: propOnSubmit,
    savePaymentAccount: propSavePaymentAccount,
    isCreate: propIsCreate,
    t
}) => {
    const [currentPaymentId, setCurrentPaymentId] = useState(null);
    const [currentPaymentSystemId, setCurrentPaymentSystemId] = useState(null);
    const [currentPaymentSystem, setCurrentPaymentSystem] = useState(null);
    const [currencies, setCurrencies] = useState([]);
    const [fullName, setFullName] = useState('');
    const [edrpouIpn, setEdrpouIpn] = useState('');
    const [bankName, setBankName] = useState('');
    const [bankAccount, setBankAccount] = useState('');
    const [bic, setBic] = useState('');
    const [purposeOfPayment, setPurposeOfPayment] = useState('');
    const [iban, setIban] = useState('');
    const [cryptoCurrency, setCryptoCurrency] = useState('');
    const [cryptoNetwork, setCryptoNetwork] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [description, setDescription] = useState('');
    const [currentCurrency, setCurrentCurrency] = useState('EUR');
    const [title, setTitle] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [tags, setTags] = useState(null);

    const resetFields = useCallback(() => {
        setFullName('');
        setEdrpouIpn('');
        setBankName('');
        setBankAccount('');
        setBic('');
        setPurposeOfPayment('');
        setIban('');
        setCryptoCurrency('');
        setCryptoNetwork('');
        setCardNumber('');
        setDescription('');
        setTitle('');
    }, []);

    const applyPaymentSystemDefaults = useCallback((paymentSystem) => {
        if (!paymentSystem) {
            setCurrentPaymentSystemId('');
            setCurrentPaymentSystem('');
            setCurrencies(['EUR']);
            setCurrentCurrency('EUR');
            return;
        }

        const currenciesList = paymentSystem.currency || ['EUR'];

        setCurrentPaymentSystemId(paymentSystem.id);
        setCurrentPaymentSystem(paymentSystem);
        setCurrencies(currenciesList);
        setCurrentCurrency(currenciesList && currenciesList.length > 0 ? currenciesList[0] : 'EUR');
    }, []);

    const getCurrentPaymentInfo = useCallback((paymentId) => {
        let payment = propPayment;

        if (!payment || !payment.paymentDetails || !Array.isArray(payment.paymentDetails)) {
            return null;
        }

        let paymentMethodDetails = payment.paymentDetails.find(function(paymentDetails) {
            return paymentDetails.id === paymentId
        });

        if (!paymentMethodDetails || !paymentMethodDetails.payment_dictionary) {
            return null;
        }

        if (!payment.allowPaymentSystems || !Array.isArray(payment.allowPaymentSystems)) {
            return null;
        }

        let paymentSystem = payment.allowPaymentSystems.find(function(allowPaymentSystems) {
            return allowPaymentSystems.id === paymentMethodDetails.payment_dictionary.id
        });

        if (!paymentSystem) {
            return null;
        }

        paymentMethodDetails.currentPaymentId = paymentMethodDetails.id;
        paymentMethodDetails.currentPaymentSystemId = paymentSystem.id;
        paymentMethodDetails.currentPaymentSystem = paymentSystem;
        paymentMethodDetails.currencies = paymentSystem.currency;
        paymentMethodDetails.currentCurrency = paymentMethodDetails.currency;

        return paymentMethodDetails
    }, [propPayment]);

    const getPaymentSystemById = useCallback((paymentSystemId) => {
        if (!propPayment || !propPayment.allowPaymentSystems || !Array.isArray(propPayment.allowPaymentSystems) || propPayment.allowPaymentSystems.length === 0) {
            return null;
        }

        let paymentSystems = propPayment.allowPaymentSystems;
        var returnPaymentSystem = paymentSystems[0];

        paymentSystems.forEach(function(paymentSystem) {
            if (parseInt(paymentSystem.id) === parseInt(paymentSystemId)) {
                returnPaymentSystem = paymentSystem;
            }
        });

        return returnPaymentSystem;
    }, [propPayment]);

    const previousVisibleRef = useRef(propVisible);

    useEffect(() => {
        const wasVisible = previousVisibleRef.current;

        if (
            propVisible &&
            !wasVisible &&
            propIsCreate &&
            !(propPayment && propPayment.currentPaymentId)
        ) {
            setCurrentPaymentId(null);
            resetFields();

            let defaultPaymentSystem = null;

            if (currentPaymentSystemId) {
                defaultPaymentSystem = getPaymentSystemById(currentPaymentSystemId);
            }

            if (!defaultPaymentSystem) {
                defaultPaymentSystem = getPaymentSystemById(null);
            }

            applyPaymentSystemDefaults(defaultPaymentSystem);
            setErrors({});
        }

        previousVisibleRef.current = propVisible;
    }, [propVisible, propIsCreate, propPayment, currentPaymentSystemId, getPaymentSystemById, applyPaymentSystemDefaults, resetFields]);

    useEffect(() => {
        const payment = propPayment || {};
        const allowPaymentSystems = payment.allowPaymentSystems || [];
        const currentPaymentIdValue = payment.currentPaymentId;

        if (currentPaymentIdValue) {
            const currentPaymentInfo = getCurrentPaymentInfo(currentPaymentIdValue);

            if (!currentPaymentInfo) {
                setCurrentPaymentId(null);
                if (allowPaymentSystems.length && getPaymentSystemById(null)) {
                    applyPaymentSystemDefaults(getPaymentSystemById(null));
                } else {
                    applyPaymentSystemDefaults(null);
                }
                resetFields();
            } else {
                setCurrentPaymentId(currentPaymentIdValue);
                setCurrentPaymentSystemId(currentPaymentInfo.currentPaymentSystemId);
                setCurrentPaymentSystem(currentPaymentInfo.currentPaymentSystem);
                setCurrencies(currentPaymentInfo.currencies);
                setFullName(currentPaymentInfo.full_name || '');
                setEdrpouIpn(currentPaymentInfo.edrpou_ipn || '');
                setBankName(currentPaymentInfo.bank_name || '');
                setBankAccount(currentPaymentInfo.bank_account || '');
                setBic(currentPaymentInfo.bic || '');
                setPurposeOfPayment(currentPaymentInfo.purpose_of_payment || '');
                setIban(currentPaymentInfo.iban || '');
                setCryptoCurrency(currentPaymentInfo.crypto_currency || '');
                setCryptoNetwork(currentPaymentInfo.crypto_network || '');
                setCardNumber(currentPaymentInfo.card_number || '');
                setDescription(currentPaymentInfo.description || '');
                setCurrentCurrency(currentPaymentInfo.currency || 'EUR');
                setTitle(currentPaymentInfo.title || '');
            }
        } else {
            if (!currentPaymentSystemId) {
                if (
                    allowPaymentSystems.length &&
                    getPaymentSystemById(null)
                ) {
                    applyPaymentSystemDefaults(getPaymentSystemById(null));
                } else {
                    applyPaymentSystemDefaults(null);
                }
            }

            if (!propIsCreate || !currentPaymentSystemId) {
                setCurrentPaymentId(null);
                resetFields();
            }
        }

        setErrors({});
    }, [propPayment, getCurrentPaymentInfo, getPaymentSystemById, propIsCreate, currentPaymentSystemId, applyPaymentSystemDefaults, resetFields]);

    const onSuccessSave = useCallback(() => {
        setLoading(false);
        propOnSubmit();
    }, [propOnSubmit]);

    const onErrorSave = useCallback((errors) => {
        setLoading(false);
        setErrors(errors);
    }, []);

    const handleSaving = useCallback((e) => {
        e.preventDefault();

        setLoading(true);

        let params = {};

        params.currency = currentCurrency;
        params.currentPaymentId = currentPaymentId;
        params.paymentSystemId = currentPaymentSystemId;
        params.paymentSystem = currentPaymentSystem;
        params.fullName = fullName;
        params.edrpouIpn = edrpouIpn;
        params.bankName = bankName;
        params.bankAccount = bankAccount;
        params.bic = bic;
        params.purposeOfPayment = purposeOfPayment;
        params.iban = iban;
        params.cryptoCurrency = cryptoCurrency;
        params.cryptoNetwork = cryptoNetwork;
        params.cardNumber = cardNumber;
        params.description = description;
        params.title = title;

        propSavePaymentAccount(params, onSuccessSave, onErrorSave);
    }, [currentCurrency, currentPaymentId, currentPaymentSystemId, currentPaymentSystem, fullName, edrpouIpn, bankName, bankAccount, bic, purposeOfPayment, iban, cryptoCurrency, cryptoNetwork, cardNumber, description, title, propSavePaymentAccount, onSuccessSave, onErrorSave]);

    const setPaymentSystem = useCallback((event) => {
        if (!propIsCreate) {
            return;
        }

        const currentPaymentSystemId = event.target.value;
        const currentPaymentSystem = getPaymentSystemById(currentPaymentSystemId);

        if (!currentPaymentSystem) {
            return;
        }

        const currencies = currentPaymentSystem.currency || ['EUR'];

        setCurrentPaymentSystemId(currentPaymentSystemId);
        setCurrentPaymentSystem(currentPaymentSystem);
        setCurrencies(currencies);
        setCurrentCurrency(currencies && currencies.length > 0 ? currencies[0] : 'EUR');
        setFullName('');
        setEdrpouIpn('');
        setBankName('');
        setBankAccount('');
        setBic('');
        setPurposeOfPayment('');
        setIban('');
        setCryptoCurrency('');
        setCryptoNetwork('');
        setCardNumber('');
        setDescription('');
        setErrors({});
    }, [propIsCreate, getPaymentSystemById]);

    const getPaymentSystemClasses = useCallback((paymentSystemId) => {
        return classNames({
            'btn': true,
            'dim': true,
            'btn-primary': true,
            'item': true,
            'active': parseInt(paymentSystemId) === parseInt(currentPaymentSystemId)
        })
    }, [currentPaymentSystemId]);

    const staticNames = t?.paymentPage?.settingModal || {};
    const staticNamesActions = t?.actions || {};

    return (
        <CustomModal
            visible={propVisible}
            onClose={propOnClose}
            confirmName={staticNamesActions.save}
            loading={loading}
            isForm={true}
            title={propIsCreate ? staticNames.addAccount : staticNames.editAccount}
            onSubmit={handleSaving}
        >
            <div className="form-horizontal payment-method-form">
                <FormGroup
                    label={`${staticNames.accountName}`}
                    labelClass="required"
                    errors={errors && errors.title}
                >
                    <input
                        type="text"
                        className="form-control"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                    />
                </FormGroup>
                <FormGroup label={`${staticNames.accountType}:`}>
                    <div className="payment-method__methods-list">
                        {propPayment && propPayment.allowPaymentSystems && Array.isArray(propPayment.allowPaymentSystems) && propPayment.allowPaymentSystems.map(item => (
                            <div
                                className="list-item"
                                key={item.id}
                                data-id={item.id}
                            >
                                <input type="radio"
                                       name="paymentSystem"
                                       className={getPaymentSystemClasses(item.id)}
                                       value={item.id}
                                       onChange={setPaymentSystem}
                                       checked={parseInt(item.id) === parseInt(currentPaymentSystemId)}
                                       disabled={!propIsCreate}
                                />
                                {item.icon
                                    ? (
                                        <span className="payment-method__icon" style={paymentMethodIconStyle}>
                                            <Icon
                                                alt={item.name}
                                                src={item.icon}
                                                className={item.sysName || ''}
                                            />
                                        </span>
                                    )
                                    : null
                                }
                                <span>{item.name}</span>
                            </div>
                        ))}
                    </div>
                </FormGroup>
                <FormGroup label={`${staticNames.accountCurrency}:`}>
                    <DropdownList
                        className={'rw-dropdownlist-select'}
                        disabled={!propIsCreate}
                        data={currencies}
                        value={currentCurrency}
                        onChange={setCurrentCurrency}
                    />
                </FormGroup>
                <PaymentSystem
                    onChangeFullName={(e, value) => setFullName(value === undefined ? e.target.value : value)}
                    onChangeEdrpouIpn={(e, value) => setEdrpouIpn(value === undefined ? e.target.value : value)}
                    onChangeBankAccount={(e, value) => setBankAccount(value === undefined ? e.target.value : value)}
                    onChangeBankName={(e, value) => setBankName(value === undefined ? e.target.value : value)}
                    onChangeBic={(e, value) => setBic(value === undefined ? e.target.value : value)}
                    onChangePurposeOfPayment={(e, value) => setPurposeOfPayment(value === undefined ? e.target.value : value)}
                    onChangeIban={(e, value) => setIban(value === undefined ? e.target.value : value)}
                    onChangeCryptoCurrency={(e, value) => setCryptoCurrency(value === undefined ? e.target.value : value)}
                    onChangeCryptoNetwork={(e, value) => setCryptoNetwork(value === undefined ? e.target.value : value)}
                    onChangeCardNumber={(e, value) => setCardNumber(value === undefined ? e.target.value : value)}
                    onChangeDescription={(e, value) => setDescription(value === undefined ? e.target.value : value)}
                    fullName={fullName}
                    edrpouIpn={edrpouIpn}
                    bankName={bankName}
                    bankAccount={bankAccount}
                    bic={bic}
                    purposeOfPayment={purposeOfPayment}
                    iban={iban}
                    cryptoCurrency={cryptoCurrency}
                    cryptoNetwork={cryptoNetwork}
                    cardNumber={cardNumber}
                    description={description}
                    currentPaymentId={currentPaymentId}
                    id={currentPaymentSystemId}
                    paymentSystem={getPaymentSystemById(currentPaymentSystemId)}
                    countries={propPayment && propPayment.countries ? propPayment.countries : []}
                    errors={errors}
                />
            </div>
        </CustomModal>
    );
};

PaymentPageSettingModal.propTypes = {
    payment: PropTypes.object,
    visible: PropTypes.bool,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    savePaymentAccount: PropTypes.func,
    isCreate: PropTypes.bool,
    t: PropTypes.object
};

PaymentPageSettingModal.defaultProps = {
    payment: {},
    visible: false,
    onClose: () => {},
    onSubmit: () => {},
    savePaymentAccount: () => {},
    isCreate: false
};

const paymentMethodIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    margin: '0 6px',
    verticalAlign: 'middle',
    overflow: 'hidden'
};

const iconImageStyle = {
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    display: 'block'
};

const iconSvgStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

function Icon({ alt, src, className}) {
    const isSvgMarkup = typeof src === 'string' && src.trim().startsWith('<');
    if (isSvgMarkup) {
        return <div className={className} style={iconSvgStyle} dangerouslySetInnerHTML={{ __html: src }}></div>;
    }
    return <img className={className} src={src} alt={alt} style={iconImageStyle}/>;
}

export default withTranslation(PaymentPageSettingModal);
